import React from 'react';
import { Wrench, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation, Trans } from 'react-i18next';

export const Hero = ({ onBookClick }) => {
  const { t } = useTranslation();

  // Array de traducciones para los features
  const features = [t('feature_1'), t('feature_2'), t('feature_3')];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-neutral-950">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-lime-900/20 via-neutral-950 to-neutral-950"></div>
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-lime-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-lime-600/10 rounded-full blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="text-center space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-lime-500/10 border border-lime-500/30 rounded-full px-4 py-2">
            <Wrench className="h-4 w-4 text-lime-400" />
            <span className="text-lime-400 text-sm font-semibold">{t('hero_badge')}</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight">
            <Trans i18nKey="hero_title">
              Expert <span className="text-lime-400">Electronic</span>
              <br />
              Diagnostics & ECU Programming
            </Trans>
          </h1>

          {/* Subheading */}
          <p className="text-xl sm:text-2xl text-neutral-300 max-w-3xl mx-auto leading-relaxed">
            {t('hero_subtitle')}
          </p>

          {/* Features List */}
          <div className="flex flex-wrap justify-center gap-6 pt-4">
            {features.map((feature) => (
              <div key={feature} className="flex items-center space-x-2 text-neutral-200">
                <CheckCircle className="h-5 w-5 text-lime-400" />
                <span className="font-medium">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            <Button
              onClick={onBookClick}
              className="bg-lime-500 hover:bg-lime-600 text-neutral-900 font-bold text-lg px-8 py-6 transition-all duration-300 hover:scale-105 shadow-lg shadow-lime-500/30"
            >
              {t('btn_book')}
            </Button>
            <Button
              onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              className="border-2 border-neutral-700 hover:border-lime-400 text-white hover:text-lime-400 font-bold text-lg px-8 py-6 bg-transparent transition-all duration-300"
            >
              {t('btn_services')}
            </Button>
          </div>

          {/* Trust Badge */}
          <div className="pt-12">
            <p className="text-neutral-400 text-sm mb-3">{t('trust_text')}</p>
            <div className="flex items-center justify-center space-x-1">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-6 h-6 text-lime-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
              <span className="ml-3 text-neutral-300 font-semibold">{t('rating')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-neutral-900 to-transparent"></div>
    </section>
  );
};
